import React, { useState, useEffect } from 'react';
import TemplateDownload from './TemplateDownload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

function Vehicles({ token }) {
  const isMobile = useIsMobile();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showSinYenUpload, setShowSinYenUpload] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [message, setMessage] = useState('');
  const [expandedVehicle, setExpandedVehicle] = useState(null); // For mobile expand/collapse
  const [departmentsProjects, setDepartmentsProjects] = useState({ departments: [], projects: [] });
  
  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferVehicle, setTransferVehicle] = useState(null);
  const [transferHistory, setTransferHistory] = useState([]);
  const [transferData, setTransferData] = useState({
    effective_date: new Date().toISOString().split('T')[0],
    new_user: '',
    new_department: '',
    new_project: '',
    new_person_in_charge: '',
    new_person_in_charge_email: '',
    new_person_in_charge_phone: '',
    reason: ''
  });
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Servicing profiles
  const [servicingProfiles, setServicingProfiles] = useState([]);
  
  const [formData, setFormData] = useState({
    vehicle_no: '',
    owner: '',
    user: '',
    department: '',
    project: '',
    person_in_charge: '',
    person_in_charge_email: '',
    person_in_charge_phone: '',
    vehicle_type: '',
    vehicle_model: '',
    max_laden_weight: '',
    vehicle_make: '',
    tonnage: '',
    // Additional details
    remarks: '',
    emission_standard: '',
    fuel_tank_capacity: '',
    year_of_manufacture: '',
    scrap_value: '',
    coe_rebate: '',
    registration_date: '',
    // Original COE Section
    original_reg_date: '',
    coe_expiry_date: '',
    purchase_price: '',
    coe_cost: '',
    depreciation_tenure: '',
    monthly_depreciation: '',
    yearly_depreciation: '',
    // COE Renewal Section
    coe_renewal_date: '',
    renewal_term: '',
    renewal_price: '',
    coe_expiry_after_renewal: '',
    new_depreciation_tenure: '',
    new_monthly_depreciation: '',
    new_yearly_depreciation: '',
    // Income
    daily_income: '',
    // Servicing Schedule
    servicing_profile_id: '',
    minor_service_interval_months: '',
    minor_service_interval_km: '',
    major_service_interval_months: '',
    major_service_interval_km: '',
    last_service_date: '',
    last_service_mileage: '',
    last_service_type: '',
    current_mileage: ''
  });

  useEffect(() => {
    fetchVehicles();
    fetchDepartmentsProjects();
    fetchServicingProfiles();
    fetchMRUploadStatus();
  }, []);

  // State for M&R upload status
  const [mrUploadStatus, setMrUploadStatus] = useState({});

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVehicles(data);
      setFilteredVehicles(data); // Initialize filtered list
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMRUploadStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/mr/upload-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Convert to lookup object
        const statusMap = {};
        data.vehicles.forEach(v => {
          statusMap[v.vehicle_no] = v;
        });
        setMrUploadStatus(statusMap);
      }
    } catch (error) {
      console.error('Error fetching M&R status:', error);
    }
  };

  const formatMRDate = (dateStr) => {
    if (!dateStr) return 'No data';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-SG', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  const fetchDepartmentsProjects = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/meta/departments-projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDepartmentsProjects(data);
      }
    } catch (error) {
      console.error('Error fetching departments/projects:', error);
    }
  };

  const fetchServicingProfiles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/servicing-profiles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Only include multi-level profiles
        setServicingProfiles(data.filter(p => p.levels && p.levels.length > 0));
      }
    } catch (error) {
      console.error('Error fetching servicing profiles:', error);
    }
  };

  // Filter vehicles based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredVehicles(vehicles);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = vehicles.filter(v => 
        v.vehicle_no?.toLowerCase().includes(term) ||
        v.owner?.toLowerCase().includes(term) ||
        v.user?.toLowerCase().includes(term) ||
        v.department?.toLowerCase().includes(term) ||
        v.project?.toLowerCase().includes(term) ||
        v.vehicle_make?.toLowerCase().includes(term) ||
        v.vehicle_model?.toLowerCase().includes(term) ||
        v.vehicle_type?.toLowerCase().includes(term)
      );
      setFilteredVehicles(filtered);
    }
  }, [searchTerm, vehicles]);

  // Auto-calculate depreciation based on (Purchase Price + COE Cost) / (Tenure × 12)
  const calculateDepreciation = (purchasePrice, coeCost, tenure) => {
    const price = parseFloat(purchasePrice) || 0;
    const coe = parseFloat(coeCost) || 0;
    const years = parseFloat(tenure) || 0;
    
    if (price > 0 && years > 0) {
      const totalCost = price + coe;
      const monthly = totalCost / (years * 12);
      const yearly = monthly * 12;
      return { monthly: monthly.toFixed(2), yearly: yearly.toFixed(2) };
    }
    return null;
  };

  // Update form with auto-calculated depreciation
  const updateFormWithDepreciation = (updates) => {
    const newData = { ...formData, ...updates };
    const depreciation = calculateDepreciation(
      newData.purchase_price, 
      newData.coe_cost, 
      newData.depreciation_tenure
    );
    
    if (depreciation) {
      newData.monthly_depreciation = depreciation.monthly;
      newData.yearly_depreciation = depreciation.yearly;
    }
    
    setFormData(newData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanedData = {};
      const numericFields = [
        'max_laden_weight', 'tonnage', 'purchase_price', 'coe_cost',
        'depreciation_tenure', 'monthly_depreciation', 'yearly_depreciation',
        'renewal_term', 'renewal_price', 'new_depreciation_tenure', 'new_monthly_depreciation', 'new_yearly_depreciation',
        'daily_income', 'minor_service_interval_months', 'minor_service_interval_km',
        'major_service_interval_months', 'major_service_interval_km',
        'last_service_mileage', 'current_mileage', 'fuel_tank_capacity', 'year_of_manufacture',
        'scrap_value', 'coe_rebate'
      ];
      
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        if (numericFields.includes(key)) {
          // Numeric fields: convert to number or null
          cleanedData[key] = (value !== '' && value !== null && value !== undefined) ? parseFloat(value) : null;
        } else {
          // String fields: keep as string (empty string is fine)
          cleanedData[key] = value || '';
        }
      });

      // Auto-calculate yearly depreciation from monthly
      if (cleanedData.monthly_depreciation && !cleanedData.yearly_depreciation) {
        cleanedData.yearly_depreciation = cleanedData.monthly_depreciation * 12;
      }
      if (cleanedData.new_monthly_depreciation && !cleanedData.new_yearly_depreciation) {
        cleanedData.new_yearly_depreciation = cleanedData.new_monthly_depreciation * 12;
      }

      // Auto-calculate COE expiry dates
      if (cleanedData.original_reg_date && !cleanedData.coe_expiry_date) {
        const regDate = new Date(cleanedData.original_reg_date);
        regDate.setFullYear(regDate.getFullYear() + 10);
        cleanedData.coe_expiry_date = regDate.toISOString().split('T')[0];
      }
      if (cleanedData.coe_renewal_date && cleanedData.renewal_term && !cleanedData.coe_expiry_after_renewal) {
        const renewalDate = new Date(cleanedData.coe_renewal_date);
        renewalDate.setFullYear(renewalDate.getFullYear() + parseInt(cleanedData.renewal_term));
        cleanedData.coe_expiry_after_renewal = renewalDate.toISOString().split('T')[0];
      }

      const url = editingVehicle 
        ? `${BACKEND_URL}/api/vehicles/${editingVehicle}`
        : `${BACKEND_URL}/api/vehicles`;
      
      const method = editingVehicle ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanedData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        let errorMsg = 'Failed to save vehicle';
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMsg = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Handle validation errors array
            errorMsg = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else {
            errorMsg = JSON.stringify(errorData.detail);
          }
        }
        throw new Error(errorMsg);
      }

      fetchVehicles();
      fetchDepartmentsProjects();
      setShowForm(false);
      setShowEditModal(false);
      setEditingVehicle(null);
      resetFormData();
      setMessage('✅ Vehicle saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message}`);
    }
  };

  const resetFormData = () => {
    setFormData({
      vehicle_no: '', owner: '', user: '', department: '', project: '', 
      person_in_charge: '', person_in_charge_email: '', person_in_charge_phone: '',
      vehicle_type: '', vehicle_model: '',
      max_laden_weight: '', vehicle_make: '', tonnage: '',
      remarks: '', emission_standard: '', fuel_tank_capacity: '',
      year_of_manufacture: '', scrap_value: '', coe_rebate: '', registration_date: '',
      original_reg_date: '', coe_expiry_date: '', purchase_price: '', coe_cost: '',
      depreciation_tenure: '', monthly_depreciation: '', yearly_depreciation: '',
      coe_renewal_date: '', renewal_term: '', renewal_price: '', coe_expiry_after_renewal: '',
      new_depreciation_tenure: '', new_monthly_depreciation: '', new_yearly_depreciation: '',
      daily_income: '',
      servicing_profile_id: '',
      minor_service_interval_months: '', minor_service_interval_km: '',
      major_service_interval_months: '', major_service_interval_km: '',
      last_service_date: '', last_service_mileage: '', last_service_type: '', current_mileage: ''
    });
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle.vehicle_no);
    setFormData({
      vehicle_no: vehicle.vehicle_no || '',
      owner: vehicle.owner || '',
      user: vehicle.user || '',
      department: vehicle.department || '',
      project: vehicle.project || '',
      person_in_charge: vehicle.person_in_charge || '',
      person_in_charge_email: vehicle.person_in_charge_email || '',
      person_in_charge_phone: vehicle.person_in_charge_phone || '',
      vehicle_type: vehicle.vehicle_type || '',
      vehicle_model: vehicle.vehicle_model || '',
      max_laden_weight: vehicle.max_laden_weight || '',
      vehicle_make: vehicle.vehicle_make || '',
      tonnage: vehicle.tonnage || '',
      // Additional details
      remarks: vehicle.remarks || '',
      emission_standard: vehicle.emission_standard || '',
      fuel_tank_capacity: vehicle.fuel_tank_capacity || '',
      year_of_manufacture: vehicle.year_of_manufacture || '',
      scrap_value: vehicle.scrap_value || '',
      coe_rebate: vehicle.coe_rebate || '',
      registration_date: vehicle.registration_date || '',
      // Original COE Section
      original_reg_date: vehicle.original_reg_date || vehicle.purchase_date || '',
      coe_expiry_date: vehicle.coe_expiry_date || '',
      purchase_price: vehicle.purchase_price || '',
      coe_cost: vehicle.coe_cost || vehicle.coe_price || '',
      depreciation_tenure: vehicle.depreciation_tenure || '',
      monthly_depreciation: vehicle.monthly_depreciation || '',
      yearly_depreciation: vehicle.yearly_depreciation || '',
      // COE Renewal Section
      coe_renewal_date: vehicle.coe_renewal_date || '',
      renewal_term: vehicle.renewal_term || '',
      renewal_price: vehicle.renewal_price || '',
      coe_expiry_after_renewal: vehicle.coe_expiry_after_renewal || '',
      new_depreciation_tenure: vehicle.new_depreciation_tenure || '',
      new_monthly_depreciation: vehicle.new_monthly_depreciation || '',
      new_yearly_depreciation: vehicle.new_yearly_depreciation || '',
      // Income
      daily_income: vehicle.daily_income || '',
      // Servicing Schedule
      servicing_profile_id: vehicle.servicing_profile_id || '',
      minor_service_interval_months: vehicle.minor_service_interval_months || vehicle.service_interval_months || '',
      minor_service_interval_km: vehicle.minor_service_interval_km || vehicle.service_interval_km || '',
      major_service_interval_months: vehicle.major_service_interval_months || '',
      major_service_interval_km: vehicle.major_service_interval_km || '',
      last_service_date: vehicle.last_service_date || '',
      last_service_mileage: vehicle.last_service_mileage || '',
      last_service_type: vehicle.last_service_type || '',
      current_mileage: vehicle.current_mileage || ''
    });
    setShowEditModal(true);
    setExpandedVehicle(null);
  };

  const handleCancelEdit = () => {
    setEditingVehicle(null);
    setShowEditModal(false);
    setShowForm(false);
    resetFormData();
  };

  // Transfer vehicle functions
  const openTransferModal = async (vehicle) => {
    setTransferVehicle(vehicle);
    setTransferData({
      effective_date: new Date().toISOString().split('T')[0],
      new_user: vehicle.user || '',
      new_department: vehicle.department || '',
      new_project: vehicle.project || '',
      new_person_in_charge: vehicle.person_in_charge || '',
      new_person_in_charge_email: vehicle.person_in_charge_email || '',
      new_person_in_charge_phone: vehicle.person_in_charge_phone || '',
      reason: ''
    });
    
    // Fetch transfer history
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicle.vehicle_no}/transfer-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransferHistory(data.transfer_history || []);
      }
    } catch (error) {
      console.error('Error fetching transfer history:', error);
    }
    
    setShowTransferModal(true);
    setExpandedVehicle(null);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferVehicle) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${transferVehicle.vehicle_no}/transfer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vehicle_no: transferVehicle.vehicle_no,
          ...transferData
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Transfer failed');
      }

      setMessage('✅ Vehicle transferred successfully');
      setShowTransferModal(false);
      setTransferVehicle(null);
      fetchVehicles();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(`❌ ${error.message}`);
    }
  };

  const handleDelete = async (vehicleNo) => {
    if (!window.confirm(`Delete vehicle ${vehicleNo}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleNo}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete');
      fetchVehicles();
      setExpandedVehicle(null);
      setMessage('✅ Vehicle deleted');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Failed to delete vehicle');
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkFile) return;

    const formData = new FormData();
    formData.append('file', bulkFile);

    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Upload failed');
      }

      const data = await response.json();
      setMessage(`✅ ${data.message}: ${data.inserted} inserted, ${data.updated} updated`);
      fetchVehicles();
      setShowBulkUpload(false);
      setBulkFile(null);
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('Error:', error);
      setMessage('❌ Error uploading file');
    }
  };

  // Sin Yen format import handler
  const handleSinYenImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setMessage('⏳ Importing Sin Yen data...');

    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/import/sinyen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Import failed');
      }

      const data = await response.json();
      setMessage(`✅ Sin Yen import complete: ${data.inserted} new, ${data.updated} updated (${data.total} total)`);
      fetchVehicles();
      setTimeout(() => setMessage(''), 5000);
    } catch (error) {
      console.error('Error:', error);
      setMessage(`❌ Import error: ${error.message}`);
    }
    
    // Reset file input
    e.target.value = '';
  };

  // Mobile Vehicle Card Component
  const VehicleCard = ({ vehicle, idx }) => {
    const isExpanded = expandedVehicle === vehicle.vehicle_no;
    
    return (
      <div 
        key={idx}
        data-testid={`vehicle-card-${idx}`}
        style={{
          background: 'white',
          borderRadius: '12px',
          marginBottom: '0.75rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #E2E8F0',
          overflow: 'hidden'
        }}
      >
        {/* Card Header - Always visible */}
        <div 
          onClick={() => setExpandedVehicle(isExpanded ? null : vehicle.vehicle_no)}
          style={{
            padding: '1rem',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: isExpanded ? '#F8FAFC' : 'white'
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <span className="vin-plate" style={{ fontSize: '0.8rem' }}>{vehicle.vehicle_no}</span>
              <span className="badge badge-active" style={{ fontSize: '0.65rem' }}>{vehicle.status}</span>
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '500', color: '#1E293B' }}>
              {vehicle.vehicle_make || 'Unknown'} {vehicle.vehicle_model || ''}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#64748B' }}>
              {vehicle.vehicle_type || 'Vehicle'}
            </div>
          </div>
          <div style={{ 
            fontSize: '1.25rem', 
            color: '#64748B',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div style={{ 
            padding: '1rem', 
            borderTop: '1px solid #E2E8F0',
            background: '#FAFBFC',
            animation: 'slideDown 0.2s ease'
          }}>
            {/* Details Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Department</div>
                <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>{vehicle.department || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Project</div>
                <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>{vehicle.project || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Purchase Date</div>
                <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>{vehicle.purchase_date || 'N/A'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Daily Income</div>
                <div style={{ fontSize: '0.85rem', fontWeight: '500', fontFamily: 'JetBrains Mono, monospace', color: '#059669' }}>
                  ${vehicle.daily_income || 500}
                </div>
              </div>
              {vehicle.purchase_price && (
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Purchase Price</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>${vehicle.purchase_price?.toLocaleString()}</div>
                </div>
              )}
              {vehicle.tonnage && (
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase' }}>Tonnage</div>
                  <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>{vehicle.tonnage} tons</div>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-secondary" 
                onClick={() => handleEdit(vehicle)}
                style={{ flex: 1, padding: '0.6rem', fontSize: '0.85rem' }}
              >
                ✏️ Edit
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => openTransferModal(vehicle)}
                style={{ flex: 1, padding: '0.6rem', fontSize: '0.85rem', background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE' }}
              >
                🔄 Transfer
              </button>
              <button 
                className="btn btn-danger" 
                onClick={() => handleDelete(vehicle.vehicle_no)}
                style={{ flex: 1, padding: '0.6rem', fontSize: '0.85rem' }}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="vehicles-page">
      <div className="page-header">
        <div style={{ marginBottom: isMobile ? '0.75rem' : '1rem' }}>
          <h2 style={{ fontSize: isMobile ? '1.25rem' : undefined }}>Fleet Vehicles</h2>
          <p style={{ fontSize: isMobile ? '0.8rem' : undefined, color: '#64748B' }}>
            {searchTerm ? `${filteredVehicles.length} of ${vehicles.length}` : vehicles.length} vehicles in fleet
          </p>
        </div>
        
        {/* Search Input */}
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: '0.75rem'
        }}>
          <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '0 1 300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="🔍 Search vehicles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="vehicle-search"
              style={{ 
                paddingLeft: '0.75rem',
                width: '100%'
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#94A3B8',
                  fontSize: '1.2rem'
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              fetch(`${BACKEND_URL}/api/vehicles/export`, {
                headers: { 'Authorization': `Bearer ${token}` }
              })
              .then(res => res.blob())
              .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `fleet_vehicles_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                setMessage('✅ Export downloaded successfully');
                setTimeout(() => setMessage(''), 3000);
              })
              .catch(err => setMessage('❌ Error exporting data'));
            }}
            data-testid="export-btn"
            style={{ 
              background: '#DCFCE7', 
              color: '#166534', 
              border: '1px solid #86EFAC',
              ...(isMobile ? { fontSize: '0.8rem', padding: '0.5rem 0.75rem' } : {})
            }}
          >
            📥 {isMobile ? 'Export' : 'Export All'}
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => { setShowSinYenUpload(!showSinYenUpload); setShowBulkUpload(false); }}
            style={{ 
              background: '#FEF3C7', 
              color: '#92400E', 
              border: '1px solid #FCD34D',
              ...(isMobile ? { fontSize: '0.8rem', padding: '0.5rem 0.75rem' } : {})
            }}
            data-testid="sinyen-import-btn"
          >
            📁 {isMobile ? 'Sin Yen' : 'Sin Yen Import'}
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => { setShowBulkUpload(!showBulkUpload); setShowSinYenUpload(false); }} 
            data-testid="bulk-upload-btn"
            style={isMobile ? { fontSize: '0.8rem', padding: '0.5rem 0.75rem' } : {}}
          >
            📊 {isMobile ? 'Bulk' : 'Bulk Upload'}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowForm(!showForm)} 
            data-testid="add-vehicle-btn"
            style={isMobile ? { fontSize: '0.8rem', padding: '0.5rem 0.75rem' } : {}}
          >
            + {isMobile ? 'Add' : 'Add Vehicle'}
          </button>
        </div>
      </div>

      {message && <div className="alert alert-success" data-testid="message">{message}</div>}

      {/* Sin Yen Import Section */}
      {showSinYenUpload && (
        <div className="card" data-testid="sinyen-upload-form" style={isMobile ? { padding: '1rem' } : {}}>
          <h3 className="card-title" style={{ color: '#92400E', ...(isMobile ? { fontSize: '1rem' } : {}) }}>
            📁 Sin Yen Format Import
          </h3>
          <p style={{marginBottom: '0.75rem', color: '#64748B', fontSize: isMobile ? '0.8rem' : undefined}}>
            Import vehicles from Sin Yen Excel format. Headers should be in row 2, data starts from row 3.
          </p>
          
          {/* Template Download Section */}
          <div style={{ 
            background: '#FEF9E7', 
            padding: '0.75rem 1rem', 
            borderRadius: '6px', 
            marginBottom: '1rem',
            border: '1px solid #FCD34D'
          }}>
            <TemplateDownload templateType="sinyen" token={token} compact={true} label="Download Sin Yen Template" />
            <p style={{ fontSize: '0.75rem', color: '#92400E', marginTop: '0.5rem', marginBottom: 0 }}>
              Download the Sin Yen format template with expected columns (Vehicle, Owner, User, Dept, etc.)
            </p>
          </div>
          
          <div className="form-group">
            <label 
              className="btn btn-primary" 
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              📤 Select Sin Yen File
              <input 
                type="file" 
                accept=".xlsx,.xls" 
                onChange={handleSinYenImport}
                style={{ display: 'none' }}
                data-testid="sinyen-file-input"
              />
            </label>
          </div>
        </div>
      )}

      {showBulkUpload && (
        <div className="card" data-testid="bulk-upload-form" style={isMobile ? { padding: '1rem' } : {}}>
          <h3 className="card-title" style={isMobile ? { fontSize: '1rem' } : {}}>Bulk Upload Vehicles</h3>
          <p style={{marginBottom: '0.75rem', color: '#64748B', fontSize: isMobile ? '0.8rem' : undefined}}>
            Upload Excel (.xlsx, .xls) or CSV file with vehicle data.
          </p>
          
          {/* Template Download Section */}
          <div style={{ 
            background: '#F8FAFC', 
            padding: '0.75rem 1rem', 
            borderRadius: '6px', 
            marginBottom: '1rem',
            border: '1px solid #E2E8F0'
          }}>
            <TemplateDownload templateType="vehicles" token={token} compact={true} label="Download Sample Template" />
            <p style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.5rem', marginBottom: 0 }}>
              Download a sample template to see the expected format and required columns.
            </p>
          </div>
          
          <form onSubmit={handleBulkUpload}>
            <div className="form-group">
              <input 
                type="file" 
                accept=".xlsx,.xls,.csv" 
                onChange={(e) => setBulkFile(e.target.files[0])} 
                required 
                data-testid="bulk-file-input" 
              />
            </div>
            <button type="submit" className="btn btn-primary" data-testid="submit-bulk-btn">
              Upload
            </button>
          </form>
        </div>
      )}

      {/* Vehicle Form Modal - for both Add and Edit */}
      {(showForm || showEditModal) && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem 1rem',
          overflowY: 'auto'
        }}>
          <div className="modal-content card" data-testid="vehicle-form" style={{
            width: '100%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: 'auto',
            ...(isMobile ? { padding: '1rem' } : {})
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid #E2E8F0'
            }}>
              <h3 className="card-title" style={{ margin: 0, fontSize: isMobile ? '1rem' : undefined }}>
                {editingVehicle ? `Edit Vehicle: ${editingVehicle}` : 'Add New Vehicle'}
              </h3>
              <button 
                onClick={handleCancelEdit}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#64748B',
                  padding: '0.25rem 0.5rem'
                }}
              >
                ×
              </button>
            </div>
          <form onSubmit={handleSubmit}>
            {/* Basic Vehicle Info */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Vehicle Number *</label>
                <input 
                  className="form-input" 
                  value={formData.vehicle_no} 
                  onChange={(e) => setFormData({...formData, vehicle_no: e.target.value})} 
                  required 
                  disabled={editingVehicle !== null}
                  data-testid="vehicle-no-input" 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Owner</label>
                <input 
                  className="form-input" 
                  placeholder="PTC Logistics"
                  value={formData.owner} 
                  onChange={(e) => setFormData({...formData, owner: e.target.value})} 
                  data-testid="owner-input" 
                />
              </div>
              <div className="form-group">
                <label className="form-label">User</label>
                <input 
                  className="form-input" 
                  placeholder="John Tan"
                  value={formData.user} 
                  onChange={(e) => setFormData({...formData, user: e.target.value})} 
                  data-testid="user-input" 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input 
                  className="form-input" 
                  placeholder="Logistics, Distribution"
                  list="departments-list"
                  value={formData.department} 
                  onChange={(e) => setFormData({...formData, department: e.target.value})} 
                  data-testid="department-input" 
                />
                <datalist id="departments-list">
                  {departmentsProjects.departments.map((dept, idx) => (
                    <option key={idx} value={dept} />
                  ))}
                </datalist>
              </div>
              <div className="form-group">
                <label className="form-label">Project</label>
                <input 
                  className="form-input" 
                  placeholder="Project Alpha"
                  list="projects-list"
                  value={formData.project} 
                  onChange={(e) => setFormData({...formData, project: e.target.value})} 
                  data-testid="project-input" 
                />
                <datalist id="projects-list">
                  {departmentsProjects.projects.map((proj, idx) => (
                    <option key={idx} value={proj} />
                  ))}
                </datalist>
              </div>
              <div className="form-group">
                <label className="form-label">Person In Charge</label>
                <input 
                  className="form-input" 
                  placeholder="Manager name"
                  value={formData.person_in_charge} 
                  onChange={(e) => setFormData({...formData, person_in_charge: e.target.value})} 
                  data-testid="person-in-charge-input" 
                />
              </div>
              <div className="form-group">
                <label className="form-label">PIC Email</label>
                <input 
                  type="email"
                  className="form-input" 
                  placeholder="manager@company.com"
                  value={formData.person_in_charge_email} 
                  onChange={(e) => setFormData({...formData, person_in_charge_email: e.target.value})} 
                  data-testid="person-in-charge-email-input" 
                />
              </div>
              <div className="form-group">
                <label className="form-label">PIC Phone</label>
                <input 
                  className="form-input" 
                  placeholder="+65 9123 4567"
                  value={formData.person_in_charge_phone} 
                  onChange={(e) => setFormData({...formData, person_in_charge_phone: e.target.value})} 
                  data-testid="person-in-charge-phone-input" 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Vehicle Type</label>
                <input className="form-input" placeholder="Prime Mover, Lorry" value={formData.vehicle_type} onChange={(e) => setFormData({...formData, vehicle_type: e.target.value})} data-testid="vehicle-type-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Model</label>
                <input className="form-input" placeholder="500 Series" value={formData.vehicle_model} onChange={(e) => setFormData({...formData, vehicle_model: e.target.value})} data-testid="vehicle-model-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Max Laden Weight (kg)</label>
                <input type="number" className="form-input" placeholder="20000" value={formData.max_laden_weight} onChange={(e) => setFormData({...formData, max_laden_weight: e.target.value})} data-testid="max-laden-weight-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Make</label>
                <input className="form-input" placeholder="Hino, Toyota, Volvo" value={formData.vehicle_make} onChange={(e) => setFormData({...formData, vehicle_make: e.target.value})} data-testid="vehicle-make-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Tonnage</label>
                <input type="number" step="0.1" className="form-input" placeholder="10" value={formData.tonnage} onChange={(e) => setFormData({...formData, tonnage: e.target.value})} data-testid="tonnage-input" />
              </div>
            </div>
            
            {/* Additional Vehicle Details Section */}
            <div style={{ 
              marginTop: '1.5rem', 
              paddingTop: '1.5rem', 
              borderTop: '2px solid #E2E8F0'
            }}>
              <h4 style={{ 
                color: '#003366', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                📝 Additional Details
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Emission Standard</label>
                  <input className="form-input" placeholder="EURO 2/3, EURO 4" value={formData.emission_standard} onChange={(e) => setFormData({...formData, emission_standard: e.target.value})} data-testid="emission-standard-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Fuel Tank Capacity (L)</label>
                  <input type="number" className="form-input" placeholder="350" value={formData.fuel_tank_capacity} onChange={(e) => setFormData({...formData, fuel_tank_capacity: e.target.value})} data-testid="fuel-tank-capacity-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Year of Manufacture</label>
                  <input type="number" className="form-input" placeholder="2020" value={formData.year_of_manufacture} onChange={(e) => setFormData({...formData, year_of_manufacture: e.target.value})} data-testid="year-of-manufacture-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Registration Date</label>
                  <input type="date" className="form-input" value={formData.registration_date} onChange={(e) => setFormData({...formData, registration_date: e.target.value})} data-testid="registration-date-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Scrap Value ($)</label>
                  <input type="number" step="0.01" className="form-input" placeholder="5000" value={formData.scrap_value} onChange={(e) => setFormData({...formData, scrap_value: e.target.value})} data-testid="scrap-value-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">COE Rebate ($)</label>
                  <input type="number" step="0.01" className="form-input" placeholder="0" value={formData.coe_rebate} onChange={(e) => setFormData({...formData, coe_rebate: e.target.value})} data-testid="coe-rebate-input" />
                </div>
                <div className="form-group" style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                  <label className="form-label">Remarks</label>
                  <input className="form-input" placeholder="Notes about this vehicle" value={formData.remarks} onChange={(e) => setFormData({...formData, remarks: e.target.value})} data-testid="remarks-input" />
                </div>
              </div>
            </div>
            
            {/* Original COE Section */}
            <div style={{ 
              marginTop: '1.5rem', 
              paddingTop: '1.5rem', 
              borderTop: '2px solid #E2E8F0'
            }}>
              <h4 style={{ 
                color: '#003366', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                📋 Original COE & Depreciation
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Original Reg Date</label>
                  <input 
                    type="date" 
                    className="form-input" 
                    value={formData.original_reg_date} 
                    onChange={(e) => {
                      const newDate = e.target.value;
                      // Auto-calculate COE expiry (10 years)
                      let coeExpiry = formData.coe_expiry_date;
                      if (newDate && !formData.coe_expiry_date) {
                        const expiryDate = new Date(newDate);
                        expiryDate.setFullYear(expiryDate.getFullYear() + 10);
                        coeExpiry = expiryDate.toISOString().split('T')[0];
                      }
                      setFormData({...formData, original_reg_date: newDate, coe_expiry_date: coeExpiry});
                    }} 
                    data-testid="original-reg-date-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">10 Year COE Expiry Date</label>
                  <input 
                    type="date" 
                    className="form-input" 
                    value={formData.coe_expiry_date} 
                    onChange={(e) => setFormData({...formData, coe_expiry_date: e.target.value})} 
                    data-testid="coe-expiry-date-input" 
                  />
                  <small style={{ color: '#64748B', fontSize: '0.7rem' }}>Auto-calculated if empty</small>
                </div>
                <div className="form-group">
                  <label className="form-label">Purchase Price ($)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="form-input" 
                    placeholder="150000"
                    value={formData.purchase_price} 
                    onChange={(e) => updateFormWithDepreciation({ purchase_price: e.target.value })} 
                    data-testid="purchase-price-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">COE Cost ($)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="form-input" 
                    placeholder="50000"
                    value={formData.coe_cost} 
                    onChange={(e) => updateFormWithDepreciation({ coe_cost: e.target.value })} 
                    data-testid="coe-cost-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Depreciation Tenure (Years)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="5"
                    value={formData.depreciation_tenure} 
                    onChange={(e) => updateFormWithDepreciation({ depreciation_tenure: e.target.value })} 
                    data-testid="depreciation-tenure-input" 
                  />
                  <small style={{ color: '#64748B', fontSize: '0.7rem' }}>e.g., 3, 5, 7, 8 years</small>
                </div>
                <div className="form-group">
                  <label className="form-label">Monthly Depreciation ($)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="form-input" 
                    placeholder="2500"
                    value={formData.monthly_depreciation} 
                    onChange={(e) => {
                      const monthly = e.target.value;
                      const yearly = monthly ? (parseFloat(monthly) * 12).toFixed(2) : '';
                      setFormData({...formData, monthly_depreciation: monthly, yearly_depreciation: yearly});
                    }} 
                    data-testid="monthly-depreciation-input" 
                  />
                  <small style={{ color: '#64748B', fontSize: '0.7rem' }}>Auto-calc: (Price + COE) ÷ (Years × 12)</small>
                </div>
                <div className="form-group">
                  <label className="form-label">Yearly Depreciation ($)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="form-input" 
                    placeholder="30000"
                    value={formData.yearly_depreciation} 
                    onChange={(e) => setFormData({...formData, yearly_depreciation: e.target.value})} 
                    data-testid="yearly-depreciation-input" 
                  />
                  <small style={{ color: '#64748B', fontSize: '0.7rem' }}>Auto: Monthly × 12</small>
                </div>
              </div>
            </div>

            {/* COE Renewal Section */}
            <div style={{ 
              marginTop: '1.5rem', 
              paddingTop: '1.5rem', 
              borderTop: '2px solid #E2E8F0'
            }}>
              <h4 style={{ 
                color: '#003366', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                🔄 COE Renewal (if extended)
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">COE Renewal Date</label>
                  <input 
                    type="date" 
                    className="form-input" 
                    value={formData.coe_renewal_date} 
                    onChange={(e) => setFormData({...formData, coe_renewal_date: e.target.value})} 
                    data-testid="coe-renewal-date-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Renewal Term (Years)</label>
                  <select 
                    className="form-input" 
                    value={formData.renewal_term} 
                    onChange={(e) => {
                      const term = e.target.value;
                      let newExpiry = formData.coe_expiry_after_renewal;
                      if (formData.coe_renewal_date && term) {
                        const expiryDate = new Date(formData.coe_renewal_date);
                        expiryDate.setFullYear(expiryDate.getFullYear() + parseInt(term));
                        newExpiry = expiryDate.toISOString().split('T')[0];
                      }
                      setFormData({...formData, renewal_term: term, coe_expiry_after_renewal: newExpiry});
                    }}
                    data-testid="renewal-term-input"
                  >
                    <option value="">Select...</option>
                    <option value="5">5 Years</option>
                    <option value="10">10 Years</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">COE Expiry After Renewal</label>
                  <input 
                    type="date" 
                    className="form-input" 
                    value={formData.coe_expiry_after_renewal} 
                    onChange={(e) => setFormData({...formData, coe_expiry_after_renewal: e.target.value})} 
                    data-testid="coe-expiry-after-renewal-input" 
                  />
                  <small style={{ color: '#64748B', fontSize: '0.7rem' }}>Auto-calculated if empty</small>
                </div>
                <div className="form-group">
                  <label className="form-label">Renewal Price ($)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="form-input" 
                    placeholder="30000"
                    value={formData.renewal_price} 
                    onChange={(e) => setFormData({...formData, renewal_price: e.target.value})} 
                    data-testid="renewal-price-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Depreciation Tenure (Years)</label>
                  <select 
                    className="form-input" 
                    value={formData.new_depreciation_tenure} 
                    onChange={(e) => setFormData({...formData, new_depreciation_tenure: e.target.value})}
                    data-testid="new-depreciation-tenure-input"
                  >
                    <option value="">Select...</option>
                    <option value="3">3 Years</option>
                    <option value="5">5 Years</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">New Monthly Depreciation ($)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="form-input" 
                    placeholder="1500"
                    value={formData.new_monthly_depreciation} 
                    onChange={(e) => {
                      const monthly = e.target.value;
                      const yearly = monthly ? (parseFloat(monthly) * 12).toFixed(2) : '';
                      setFormData({...formData, new_monthly_depreciation: monthly, new_yearly_depreciation: yearly});
                    }} 
                    data-testid="new-monthly-depreciation-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Yearly Depreciation ($)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="form-input" 
                    placeholder="18000"
                    value={formData.new_yearly_depreciation} 
                    onChange={(e) => setFormData({...formData, new_yearly_depreciation: e.target.value})} 
                    data-testid="new-yearly-depreciation-input" 
                  />
                  <small style={{ color: '#64748B', fontSize: '0.7rem' }}>Auto: Monthly × 12</small>
                </div>
              </div>
            </div>

            {/* Daily Income */}
            <div style={{ 
              marginTop: '1.5rem', 
              paddingTop: '1.5rem', 
              borderTop: '2px solid #E2E8F0'
            }}>
              <h4 style={{ 
                color: '#003366', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                💰 Income
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Est. Daily Income ($)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="form-input" 
                    placeholder="500"
                    value={formData.daily_income} 
                    onChange={(e) => setFormData({...formData, daily_income: e.target.value})} 
                    data-testid="daily-income-input" 
                  />
                </div>
              </div>
            </div>
            
            {/* Servicing Schedule Section */}
            <div style={{ 
              marginTop: '1.5rem', 
              paddingTop: '1.5rem', 
              borderTop: '2px solid #E2E8F0'
            }}>
              <h4 style={{ 
                color: '#003366', 
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                🛠️ Servicing Schedule
              </h4>
              
              {/* Servicing Profile Selector */}
              {servicingProfiles.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div className="form-group">
                    <label className="form-label">Servicing Profile (Multi-Level)</label>
                    <select
                      className="form-input"
                      value={formData.servicing_profile_id || ''}
                      onChange={(e) => setFormData({...formData, servicing_profile_id: e.target.value || null})}
                      data-testid="servicing-profile-select"
                    >
                      <option value="">-- No Profile (Manual Schedule) --</option>
                      {servicingProfiles.map(profile => (
                        <option key={profile.profile_id} value={profile.profile_id}>
                          {profile.name} ({profile.levels?.length || 0} levels)
                        </option>
                      ))}
                    </select>
                    <small style={{ color: '#64748B', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                      Selecting a profile auto-applies its A/B/C/D service schedule. Leave empty for manual scheduling below.
                    </small>
                  </div>
                  
                  {/* Show selected profile details */}
                  {formData.servicing_profile_id && (() => {
                    const selectedProfile = servicingProfiles.find(p => p.profile_id === formData.servicing_profile_id);
                    if (!selectedProfile) return null;
                    return (
                      <div style={{ 
                        background: '#F8FAFC', 
                        borderRadius: '8px', 
                        padding: '0.75rem', 
                        marginTop: '0.5rem',
                        border: '1px solid #E2E8F0'
                      }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#003366', marginBottom: '0.5rem' }}>
                          📋 {selectedProfile.name} Service Levels:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {selectedProfile.levels?.map((level, idx) => (
                            <span 
                              key={idx}
                              style={{
                                background: ['#EEF2FF', '#F0FDF4', '#FEF3C7', '#FEE2E2'][idx % 4],
                                color: ['#4F46E5', '#16A34A', '#D97706', '#DC2626'][idx % 4],
                                padding: '0.25rem 0.6rem',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: '600'
                              }}
                            >
                              {level.name}: {level.interval_km?.toLocaleString()}km / {level.interval_months}mo
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              <p style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: '1rem', fontStyle: 'italic' }}>
                Manual schedule (used if no profile selected):
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Minor Service (Months)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="3"
                    value={formData.minor_service_interval_months} 
                    onChange={(e) => setFormData({...formData, minor_service_interval_months: e.target.value})} 
                    data-testid="minor-service-interval-months-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Minor Service (KM)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="10000"
                    value={formData.minor_service_interval_km} 
                    onChange={(e) => setFormData({...formData, minor_service_interval_km: e.target.value})} 
                    data-testid="minor-service-interval-km-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Major Service (Months)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="12"
                    value={formData.major_service_interval_months} 
                    onChange={(e) => setFormData({...formData, major_service_interval_months: e.target.value})} 
                    data-testid="major-service-interval-months-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Major Service (KM)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="50000"
                    value={formData.major_service_interval_km} 
                    onChange={(e) => setFormData({...formData, major_service_interval_km: e.target.value})} 
                    data-testid="major-service-interval-km-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Service Date</label>
                  <input 
                    type="date" 
                    className="form-input"
                    value={formData.last_service_date} 
                    onChange={(e) => setFormData({...formData, last_service_date: e.target.value})} 
                    data-testid="last-service-date-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Service Mileage (KM)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="80000"
                    value={formData.last_service_mileage} 
                    onChange={(e) => setFormData({...formData, last_service_mileage: e.target.value})} 
                    data-testid="last-service-mileage-input" 
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Service Type</label>
                  <select 
                    className="form-input" 
                    value={formData.last_service_type} 
                    onChange={(e) => setFormData({...formData, last_service_type: e.target.value})}
                    data-testid="last-service-type-input"
                  >
                    <option value="">Select...</option>
                    <option value="Minor">Minor</option>
                    <option value="Major">Major</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Current Mileage (KM)</label>
                  <input 
                    type="number" 
                    className="form-input" 
                    placeholder="85000"
                    value={formData.current_mileage} 
                    onChange={(e) => setFormData({...formData, current_mileage: e.target.value})} 
                    data-testid="current-mileage-input" 
                  />
                </div>
              </div>
            </div>
            
            <div style={{
              display: 'flex', 
              gap: '0.75rem', 
              marginTop: '1.5rem',
              padding: '1rem',
              background: '#F8FAFC',
              borderTop: '1px solid #E2E8F0',
              borderRadius: '0 0 8px 8px',
              position: 'sticky',
              bottom: 0,
              marginLeft: '-1.5rem',
              marginRight: '-1.5rem',
              marginBottom: '-1.5rem'
            }}>
              <button type="submit" className="btn btn-primary" data-testid="submit-vehicle-btn">
                {editingVehicle ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancelEdit} data-testid="cancel-edit-btn">
                Cancel
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* Vehicles List - Cards on mobile, Table on desktop */}
      {isMobile ? (
        // Mobile: Card layout with expandable details
        <div data-testid="vehicles-cards">
          {filteredVehicles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748B' }}>
              {searchTerm ? 'No vehicles match your search.' : 'No vehicles found. Add your first vehicle above.'}
            </div>
          ) : (
            filteredVehicles.map((vehicle, idx) => (
              <VehicleCard key={vehicle.vehicle_no} vehicle={vehicle} idx={idx} />
            ))
          )}
        </div>
      ) : (
        // Desktop: Table layout
        <div className="card">
          <table className="table" data-testid="vehicles-table">
            <thead>
              <tr>
                <th>Vehicle No</th>
                <th>Make/Model</th>
                <th>Type</th>
                <th>Department</th>
                <th>Project</th>
                <th>Last M&R</th>
                <th>Daily Income</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((vehicle, idx) => {
                const mrStatus = mrUploadStatus[vehicle.vehicle_no];
                return (
                <tr key={idx} data-testid={`vehicle-row-${idx}`}>
                  <td><span className="vin-plate">{vehicle.vehicle_no}</span></td>
                  <td>
                    <strong>{vehicle.vehicle_make || '-'}</strong>
                    {vehicle.vehicle_model && <span style={{ color: '#64748B', marginLeft: '0.25rem' }}>{vehicle.vehicle_model}</span>}
                  </td>
                  <td>{vehicle.vehicle_type || '-'}</td>
                  <td>{vehicle.department || <span style={{ color: '#9CA3AF' }}>-</span>}</td>
                  <td>{vehicle.project || <span style={{ color: '#9CA3AF' }}>-</span>}</td>
                  <td>
                    {mrStatus?.has_mr_data ? (
                      <span style={{ fontSize: '0.8rem', color: '#059669' }} title={`${mrStatus.total_records} records`}>
                        {formatMRDate(mrStatus.last_upload_date)}
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.8rem', color: '#9CA3AF' }}>No data</span>
                    )}
                  </td>
                  <td style={{ fontFamily: 'JetBrains Mono, monospace' }}>${vehicle.daily_income || 500}</td>
                  <td><span className="badge badge-active">{vehicle.status}</span></td>
                  <td>
                    <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => handleEdit(vehicle)}
                        data-testid={`edit-vehicle-${idx}`}
                        style={{padding: '0.5rem 0.75rem', fontSize: '0.875rem'}}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => openTransferModal(vehicle)}
                        data-testid={`transfer-vehicle-${idx}`}
                        style={{padding: '0.5rem 0.75rem', fontSize: '0.875rem', background: '#EEF2FF', color: '#4F46E5', border: '1px solid #C7D2FE'}}
                      >
                        🔄 Transfer
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleDelete(vehicle.vehicle_no)}
                        data-testid={`delete-vehicle-${idx}`}
                        style={{padding: '0.5rem 0.75rem', fontSize: '0.875rem'}}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      )}

      {/* Transfer Vehicle Modal */}
      {showTransferModal && transferVehicle && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div className="modal-content" style={{
            background: 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
          }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #E2E8F0' }}>
              <h3 style={{ margin: 0, color: '#003366', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🔄 Transfer Vehicle
                <span className="vin-plate" style={{ marginLeft: 'auto' }}>{transferVehicle.vehicle_no}</span>
              </h3>
            </div>
            
            <form onSubmit={handleTransfer} style={{ padding: '1.5rem' }}>
              {/* Current Assignment */}
              <div style={{ 
                background: '#F8FAFC', 
                padding: '1rem', 
                borderRadius: '8px', 
                marginBottom: '1.5rem',
                border: '1px solid #E2E8F0'
              }}>
                <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#64748B' }}>Current Assignment</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <div><strong>User:</strong> {transferVehicle.user || '-'}</div>
                  <div><strong>Dept:</strong> {transferVehicle.department || '-'}</div>
                  <div><strong>Project:</strong> {transferVehicle.project || '-'}</div>
                  <div><strong>PIC:</strong> {transferVehicle.person_in_charge || '-'}</div>
                  <div><strong>Email:</strong> {transferVehicle.person_in_charge_email || '-'}</div>
                </div>
              </div>

              {/* New Assignment */}
              <h4 style={{ color: '#003366', marginBottom: '1rem' }}>New Assignment</h4>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '1rem' }}>
                <div className="form-group">
                  <label className="form-label">Effective Date *</label>
                  <input 
                    type="date" 
                    className="form-input" 
                    value={transferData.effective_date}
                    onChange={(e) => setTransferData({...transferData, effective_date: e.target.value})}
                    required
                    data-testid="transfer-effective-date"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New User</label>
                  <input 
                    className="form-input" 
                    placeholder="New driver/user name"
                    value={transferData.new_user}
                    onChange={(e) => setTransferData({...transferData, new_user: e.target.value})}
                    data-testid="transfer-new-user"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Department</label>
                  <input 
                    className="form-input" 
                    placeholder="New department"
                    list="departments-list"
                    value={transferData.new_department}
                    onChange={(e) => setTransferData({...transferData, new_department: e.target.value})}
                    data-testid="transfer-new-department"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Project</label>
                  <input 
                    className="form-input" 
                    placeholder="New project"
                    list="projects-list"
                    value={transferData.new_project}
                    onChange={(e) => setTransferData({...transferData, new_project: e.target.value})}
                    data-testid="transfer-new-project"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Person In Charge</label>
                  <input 
                    className="form-input" 
                    placeholder="Manager name"
                    value={transferData.new_person_in_charge}
                    onChange={(e) => setTransferData({...transferData, new_person_in_charge: e.target.value})}
                    data-testid="transfer-new-pic"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">PIC Email</label>
                  <input 
                    type="email"
                    className="form-input" 
                    placeholder="manager@company.com"
                    value={transferData.new_person_in_charge_email}
                    onChange={(e) => setTransferData({...transferData, new_person_in_charge_email: e.target.value})}
                    data-testid="transfer-new-pic-email"
                  />
                </div>
                <div className="form-group" style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                  <label className="form-label">Reason for Transfer</label>
                  <input 
                    className="form-input" 
                    placeholder="e.g., Department restructuring, project reassignment"
                    value={transferData.reason}
                    onChange={(e) => setTransferData({...transferData, reason: e.target.value})}
                    data-testid="transfer-reason"
                  />
                </div>
              </div>

              {/* Transfer History */}
              {transferHistory.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <h4 style={{ color: '#003366', marginBottom: '0.75rem' }}>Transfer History</h4>
                  <div style={{ 
                    maxHeight: '200px', 
                    overflow: 'auto', 
                    border: '1px solid #E2E8F0', 
                    borderRadius: '8px'
                  }}>
                    <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F8FAFC', position: 'sticky', top: 0 }}>
                          <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #E2E8F0' }}>Date</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #E2E8F0' }}>From</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #E2E8F0' }}>To</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #E2E8F0' }}>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transferHistory.map((t, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '0.5rem' }}>{t.effective_date}</td>
                            <td style={{ padding: '0.5rem' }}>
                              {t.previous_user || t.previous_department || '-'}
                            </td>
                            <td style={{ padding: '0.5rem' }}>
                              {t.new_user || t.new_department || '-'}
                            </td>
                            <td style={{ padding: '0.5rem', color: '#64748B' }}>{t.reason || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => { setShowTransferModal(false); setTransferVehicle(null); }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" data-testid="confirm-transfer-btn">
                  Confirm Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vehicles;
